import { useEffect, useMemo, useRef, useState } from 'react';
import EfVideo from '@elem/ui/ef-video';
import EfSpec from '@elem/ui/ef-spec';
import classNames from 'classnames';
import { useMediaDevice } from '../../hooks';
import styles from './index.module.scss';
import { tab1Icon, tab2Icon, tab3Icon } from './Icon';
import { useLang } from '@/i18n';

export default function TheComp() {
    const { t } = useLang('Oasis.TabImg');
    const [active, setActive] = useState(0);
    // 动画
    const { isH5, isPad, isPC, isDesktop, isInit } = useMediaDevice();
    const tabs = [
        {
            label: t('tab1.title'),
            icon: tab1Icon,
            pc: 'https://websiteoss.ecoflow.com/upload/video/1735612530682/TabImg1.mp4',
            pad: 'https://websiteoss.ecoflow.com/upload/video/1735612540510/TabImg1-h5.mp4',
            posterPC: 'https://websiteoss.ecoflow.com/upload/image/1735636706185/TabImg1-poster.jpg',
            posterPad: 'https://websiteoss.ecoflow.com/upload/image/1735637622910/TabImg1-poster-h5.jpg',
            desc: t.rich('tab1.desc', {
                h: (v) => <span className="highlight">{v}</span>,
            }),
        },
        {
            label: t('tab2.title'),
            icon: tab3Icon,
            pc: 'https://websiteoss.ecoflow.com/upload/video/1735612530504/TabImg2.mp4',
            pad: 'https://websiteoss.ecoflow.com/upload/video/1735612540314/TabImg2-h5.mp4',
            posterPC: 'https://websiteoss.ecoflow.com/upload/image/1735636706348/TabImg2-poster.jpg',
            posterPad: 'https://websiteoss.ecoflow.com/upload/image/1735637623786/TabImg2-poster-h5.jpg',
            desc: t.rich('tab2.desc', {
                h: (v) => <span className="highlight">{v}</span>,
            }),
        },
        {
            label: t('tab3.title'),
            icon: tab2Icon,
            pc: 'https://websiteoss.ecoflow.com/upload/video/1735612530318/TabImg3.mp4',
            pad: 'https://websiteoss.ecoflow.com/upload/video/1735612540674/TabImg3-h5.mp4',
            posterPC: 'https://websiteoss.ecoflow.com/upload/image/1735636706555/TabImg3-poster.jpg',
            posterPad: 'https://websiteoss.ecoflow.com/upload/image/1735637623971/TabImg3-poster-h5.jpg',
            desc: t.rich('tab3.desc', {
                h: (v) => <span className="highlight">{v}</span>,
            }),
        },
    ];
    const [loaderVideo, setLoaderVideo] = useState(false);
    const currentTab = useMemo(() => (loaderVideo ? tabs[active] : { ...tabs[active], pc: '', pad: '' }), [active, loaderVideo]);

    const tabsLineRef = useRef<HTMLDivElement>(null);
    const handleChange = (e: MouseEvent, idx: number) => {
        if (!tabsLineRef.current) return;
        const target = e.target as HTMLDivElement;

        // 对准下面的条
        const { left, width } = target.getBoundingClientRect();
        const { left: parentLeft = left } = target.parentElement?.getBoundingClientRect() ?? {};
        const scrollX = (target.parentElement as HTMLDivElement).scrollLeft ?? 0;
        tabsLineRef.current.style.transform = `translateX(${left - parentLeft + scrollX}px)`;
        tabsLineRef.current.style.width = `${width}px`;

        // h5 情况下切换 tab 需要显示出来
        if (isH5) {
            target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }

        // 重新播放视频
        const video: HTMLVideoElement | null = document.querySelector(`#TabImg .video-${idx} video`);
        if (video) {
            video.currentTime = 0;
            video?.play();
        }
        setActive(idx);
    };
    // 初始化
    const init = () => {
        if (!tabsLineRef.current) return;
        const target = tabsLineRef.current.parentElement?.querySelector('.container .tabs .item.active') as HTMLDivElement;
        const { left, width } = target.getBoundingClientRect();
        const { left: parentLeft = left } = target.parentElement?.getBoundingClientRect() ?? {};
        const scrollX = (target.parentElement as HTMLDivElement).scrollLeft ?? 0;
        tabsLineRef.current.style.transform = `translateX(${left - parentLeft + scrollX}px)`;
        tabsLineRef.current.style.width = `${width}px`;
    };
    useEffect(() => {
        init();
        if (typeof window === 'undefined') return;
        window.addEventListener('resize', init);
        return () => {
            window.removeEventListener('resize', init);
        };
    }, []);

    useEffect(() => {
        const scene1 = AniContainer.create({
            triggerElement: '#TabImg', // 触发元素
            triggerHook: 1, // 触发时机 1 进入 0 离开
            duration: '200%',
            offset: isPad ? '-10%' : 0,
            onProcess: (process: number) => {
                if (process > 0.28) {
                    // 重新播放视频
                    const video: HTMLVideoElement | null = document.querySelector(`#TabImg .video-${active} video`);
                    if (video) {
                        video?.play();
                    }
                }
            },
        });
        return () => {
            scene1.destroy();
        };
    }, [isPad, active]);

    useEffect(() => {
        if (isH5) return;
        const box = document.querySelector('#TabImg') as HTMLDivElement;
        const scene2 = AniContainer.create({
            triggerElement: '#TabImg', // 触发元素
            triggerHook: 0, // 触发时机 1 进入 0 离开
            offset: box.offsetHeight * 0.5,
            duration: box.offsetHeight * 0.2,
            syncFromTo: [[box, { opacity: 1 }, { opacity: 0 }, 0.1]],
        });
        return () => {
            scene2.destroy();
        };
    }, [isH5]);

    useEffect(() => {
        const loader = AniContainer.create({
            triggerElement: '#BackupPriority',
            triggerHook: 1,
            offset: 0,
            duration: '100%',
            onEnter: () => setLoaderVideo(true),
        });

        return () => {
            loader.destroy();
        };
    }, []);

    return (
        <div className={`${styles.TheComp}`} id="TabImg">
            <EfSpec />
            <div className="container">
                <div className="video-box">
                    {tabs.map(
                        (i, idx) =>
                            isInit &&
                            (isPC || isDesktop ? (
                                <EfVideo
                                    className={` video-pc video-${idx} ${active === idx ? 'active' : ''}`}
                                    pc={i.pc}
                                    poster={i.posterPC}
                                    loop
                                    key={`pc-${idx}`}
                                    preload="none"
                                />
                            ) : (
                                <EfVideo
                                    className={` video-pad video-${idx} ${active === idx ? 'active' : ''}`}
                                    pc={i.pad}
                                    poster={i.posterPad}
                                    loop
                                    key={`pad-${idx}`}
                                    preload="none"
                                />
                            )),
                    )}
                </div>
                <div className="tabs">
                    {tabs.map((i, idx) => (
                        <div className={classNames('item', { active: active === idx })} onClick={(e) => handleChange(e, idx)} key={idx}>
                            {i.icon}
                            <span>{i.label}</span>
                        </div>
                    ))}
                    <div className="tabs-line" ref={tabsLineRef}></div>
                </div>
                <div className="desc">{currentTab.desc}</div>
            </div>
        </div>
    );
}
